import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Clock, Sparkles, CheckCircle2, PlayCircle, Loader2, Star, ArrowLeft, BookOpen } from "lucide-react";
import { Mascot } from "@/components/lesson/Mascot";
import { getTopicMetadata } from "@/lib/topicMetadata";
import { StudentEmptyState } from "@/components/StudentEmptyState";

interface LessonSummary {
  id: string;
  title: string;
  slug: string;
  subject: string;
  topic: string;
  description: string | null;
  emoji: string | null;
  estimatedMinutes: number;
  xpReward: number;
  checkCount: number;
  progress: { status: "not_started" | "in_progress" | "completed"; score: number; totalChecks: number; xpAwarded: number } | null;
}

interface LessonStats { total: number; completed: number; inProgress: number; xpEarned: number }

export default function LessonLibrary() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [subjectFilter, setSubjectFilter] = useState<string>("all");

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const { data: lessons = [], isLoading } = useQuery<LessonSummary[]>({ queryKey: ["/api/lessons"], enabled: isAuthenticated });
  const { data: stats } = useQuery<LessonStats>({ queryKey: ["/api/lessons/stats"], enabled: isAuthenticated });

  const subjects = useMemo(() => Array.from(new Set(lessons.map(l => l.subject))), [lessons]);
  const visible = subjectFilter === "all" ? lessons : lessons.filter(l => l.subject === subjectFilter);
  const grouped = useMemo(() => {
    const map = new Map<string, Map<string, LessonSummary[]>>();
    for (const l of visible) {
      if (!map.has(l.subject)) map.set(l.subject, new Map());
      const topics = map.get(l.subject)!;
      if (!topics.has(l.topic)) topics.set(l.topic, []);
      topics.get(l.topic)!.push(l);
    }
    return map;
  }, [visible]);

  const continueLesson = lessons.find(l => l.progress?.status === "in_progress") || lessons.find(l => !l.progress || l.progress.status === "not_started");
  const completedPct = stats && stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-primary text-primary-foreground p-4 md:p-6">
        <div className="container max-w-5xl mx-auto">
          <Button variant="ghost" className="text-white hover:bg-white/20 p-2 mb-2" onClick={() => setLocation("/dashboard")} data-testid="button-back-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Sparkles className="w-6 h-6" /> Interactive Lessons</h1>
              <p className="text-white/90 mt-1">Learn by doing: sort, match, drag, tap and solve. Earn XP for every lesson you finish.</p>
            </div>
            {stats && stats.total > 0 && (
              <div className="bg-white/15 rounded-2xl px-4 py-3 min-w-[220px]">
                <div className="flex justify-between text-sm mb-1"><span>{stats.completed}/{stats.total} completed</span><span>{completedPct}%</span></div>
                <Progress value={completedPct} className="h-2 bg-white/25" />
                <p className="text-xs text-white/80 mt-1">{stats.xpEarned} XP earned from lessons</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        {continueLesson && (
          <Card className="rounded-2xl border-primary/30 bg-card">
            <CardContent className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <Mascot mood="happy" size="md" message={continueLesson.progress?.status === "in_progress" ? `Pick up where you left off: **${continueLesson.title}**` : `Ready for something new? Try **${continueLesson.title}**`} className="flex-1" />
              <Button className="rounded-xl font-bold" onClick={() => setLocation(`/lessons/${continueLesson.slug}`)} data-testid="button-lesson-continue-hero">
                <PlayCircle className="w-4 h-4 mr-2" /> {continueLesson.progress?.status === "in_progress" ? "Continue" : "Start"}
              </Button>
            </CardContent>
          </Card>
        )}

        {subjects.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {["all", ...subjects].map(s => (
              <button key={s} type="button" onClick={() => setSubjectFilter(s)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold border-2 transition-colors ${subjectFilter === s ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}
                data-testid={`filter-subject-${s}`}>
                {s === "all" ? "All subjects" : s}
              </button>
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : lessons.length === 0 ? (
          <Card className="rounded-xl"><CardContent className="p-4 md:p-5">
            <StudentEmptyState
              illustration="owl-book"
              title="No lessons for your grade yet"
              hint="Owlbert is still writing them. Practise with a quiz in the meantime."
              action={<Button className="rounded-lg" onClick={() => setLocation("/learning")}>Go to Subjects</Button>}
            />
          </CardContent></Card>
        ) : (
          Array.from(grouped.entries()).map(([subject, topics]) => {
            const meta = getTopicMetadata(subject, "subject");
            return (
              <section key={subject} className="space-y-3">
                <h2 className="text-lg font-semibold">{subject}</h2>
                {Array.from(topics.entries()).map(([topic, items]) => (
                  <div key={topic} className="space-y-2">
                    <p className="text-sm font-semibold text-muted-foreground">{topic}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {items.map(l => <LessonCard key={l.id} lesson={l} onOpen={() => setLocation(`/lessons/${l.slug}`)} />)}
                    </div>
                  </div>
                ))}
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}

export function LessonCard({ lesson, onOpen, compact = false }: { lesson: LessonSummary; onOpen: () => void; compact?: boolean }) {
  const status = lesson.progress?.status ?? "not_started";
  return (
    <Card className="hover-elevate cursor-pointer rounded-2xl transition-transform hover:-translate-y-0.5" onClick={onOpen} data-testid={`card-lesson-${lesson.slug}`}>
      <CardContent className={compact ? "p-4" : "p-5"}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            {lesson.emoji ? <span className="text-lg leading-none">{lesson.emoji}</span> : <BookOpen className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-base leading-tight">{lesson.title}</h3>
              {status === "completed" && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-0"><CheckCircle2 className="w-3 h-3 mr-1" /> Done</Badge>}
              {status === "in_progress" && <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border-0"><PlayCircle className="w-3 h-3 mr-1" /> In progress</Badge>}
            </div>
            {lesson.description && !compact && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{lesson.description}</p>}
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {lesson.estimatedMinutes} min</span>
              <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5" /> {lesson.checkCount} checks</span>
              <span className="flex items-center gap-1 text-primary font-semibold"><Sparkles className="w-3.5 h-3.5" /> {status === "completed" && lesson.progress ? `${lesson.progress.xpAwarded} XP earned` : `+${lesson.xpReward} XP`}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
